import React, { useContext, useEffect, useRef, useState } from 'react'
import { QueryParamContext } from './QueryParamContextProvider'
import { AbandonmentStorefrontEventData, PREVIEW_EVENT_ID } from '../types'
import { ViddyLPContext } from './viddyLPContext'
import { LandingPageChannel } from '@storefronts/common-types'

export type UserEventDataContext = {
    abandonmentStorefrontData: AbandonmentStorefrontEventData | null
    initialized: boolean
}

export const UserEventDataContext = React.createContext<UserEventDataContext>({
    abandonmentStorefrontData: null,
    initialized: false,
})

export const UserEventDataContextProvider: React.FC<{
    children: React.ReactNode
}> = ({ children }) => {
    const requestSent = useRef(false)
    const { EVENT_ID, P_EVENT_ID, SHOP, IS_PREVIEW } =
        useContext(QueryParamContext)

    const { landingPage } = useContext(ViddyLPContext)

    const isOwnedChannel = landingPage?.channel === LandingPageChannel.OWNED
    const needsEventData =
        !!(EVENT_ID || P_EVENT_ID || IS_PREVIEW) && isOwnedChannel

    // GET_PARAM cannot read the URL during SSR, so needsEventData is unreliable
    // there. Owned channels may need event data after hydration — start
    // uninitialized so consumers show loading instead of an empty state.
    const [data, setData] = useState<UserEventDataContext>({
        abandonmentStorefrontData: null,
        initialized: !isOwnedChannel,
    })

    useEffect(() => {
        if (!needsEventData) {
            setData({
                abandonmentStorefrontData: null,
                initialized: true,
            })
            return
        }
        if (requestSent.current) {
            return
        }
        let eventId = EVENT_ID
        if (!EVENT_ID && !P_EVENT_ID && IS_PREVIEW) {
            eventId = PREVIEW_EVENT_ID
        }
        requestSent.current = true
        const apiPath =
            (process.env.NEXT_PUBLIC_API_PREFIX ?? '') +
            '/api/abandonment-event-data'

        fetch(apiPath, {
            method: 'POST',
            body: JSON.stringify({
                shop: SHOP || landingPage?.client?.shopifyShop?.split('.')?.[0],
                eventId,
                pEventId: P_EVENT_ID,
                clientHash: landingPage?.client?.clientHash,
            }),
        })
            .then((r) => r.json())
            .then((r) =>
                setData({
                    abandonmentStorefrontData: r?.eventData,
                    initialized: true,
                }),
            )
            .catch(() => {
                setData({
                    abandonmentStorefrontData: null,
                    initialized: true,
                })
            })
    }, [
        needsEventData,
        EVENT_ID,
        P_EVENT_ID,
        IS_PREVIEW,
        SHOP,
        landingPage?.client?.shopifyShop,
        landingPage?.client?.clientHash,
    ])

    return (
        <UserEventDataContext.Provider value={data}>
            {children}
        </UserEventDataContext.Provider>
    )
}
